import { useState } from 'react';
import { GripVertical, Pencil, Trash2, Check, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import type { CurriculumModule } from '@/lib/api/curriculum';

const phaseColors: Record<string, string> = {
  fundamentals: '#94a3b8',
  frontend: '#22d3ee',
  backend: '#4ade80',
  fullstack: '#a78bfa',
};

interface ModuleListItemProps {
  module: CurriculumModule;
  selected: boolean;
  onClick: () => void;
  onUpdateModule: (id: string, data: { title?: string; phase?: string }) => void;
  onDeleteModule: (id: string) => void;
}

export function ModuleListItem({
  module, selected, onClick, onUpdateModule, onDeleteModule,
}: ModuleListItemProps) {
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(module.title);
  const [editPhase, setEditPhase] = useState(module.phase);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const topicCount = module.topics?.length ?? 0;

  const handleSave = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    if (!editTitle.trim()) return;
    onUpdateModule(module.id, { title: editTitle.trim(), phase: editPhase });
    setEditing(false);
  };

  const handleCancelEdit = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    setEditTitle(module.title);
    setEditPhase(module.phase);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="space-y-1.5 px-3 py-2" onClick={(e) => e.stopPropagation()}>
        <input
          value={editTitle}
          onChange={(e) => setEditTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSave(e);
            if (e.key === 'Escape') handleCancelEdit(e);
          }}
          className="w-full rounded border border-border bg-background px-2 py-1 text-sm"
          autoFocus
        />
        <div className="flex items-center gap-1.5">
          <select
            value={editPhase}
            onChange={(e) => setEditPhase(e.target.value as typeof editPhase)}
            className="flex-1 rounded border border-border bg-background px-2 py-1 text-xs"
          >
            <option value="fundamentals">Fundamentals</option>
            <option value="frontend">Frontend</option>
            <option value="backend">Backend</option>
            <option value="fullstack">Fullstack</option>
          </select>
          <button onClick={handleSave} className="text-green-600 hover:text-green-700" title="Save">
            <Check className="h-4 w-4" />
          </button>
          <button onClick={handleCancelEdit} className="text-muted-foreground" title="Cancel">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  if (deleteConfirm) {
    return (
      <div className="flex items-center gap-1.5 px-3 py-2" onClick={(e) => e.stopPropagation()}>
        <span className="flex-1 text-xs font-medium text-destructive">Delete?</span>
        <button
          onClick={() => onDeleteModule(module.id)}
          className="rounded bg-destructive px-2 py-0.5 text-xs text-white hover:bg-destructive/90"
        >Yes</button>
        <button
          onClick={() => setDeleteConfirm(false)}
          className="rounded border border-border px-2 py-0.5 text-xs hover:bg-muted"
        >No</button>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'group flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 transition-colors',
        selected ? 'bg-orange-50 text-orange-700' : 'text-foreground hover:bg-muted/50',
      )}
      onClick={onClick}
    >
      <GripVertical className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground/30" />
      <div
        className="h-2 w-2 flex-shrink-0 rounded-full"
        style={{ backgroundColor: phaseColors[module.phase] }}
      />
      <span className="w-5 flex-shrink-0 text-right text-xs text-muted-foreground">{module.sort_order}.</span>
      <span className={cn('flex-1 truncate text-sm font-medium', selected ? 'text-orange-700' : '')}>
        {module.title}
      </span>
      <span className="flex-shrink-0 text-xs text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100">
        {topicCount}t
      </span>
      <div className="flex flex-shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
        <button
          onClick={(e) => { e.stopPropagation(); setEditing(true); }}
          className="rounded p-0.5 text-muted-foreground hover:text-foreground"
          title="Edit module"
        >
          <Pencil className="h-3 w-3" />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); setDeleteConfirm(true); }}
          className="rounded p-0.5 text-muted-foreground hover:text-destructive"
          title="Delete module"
        >
          <Trash2 className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}
